/** PLAY FROM ANYWHERE — the venue's own switch and numbers, plus the hand
 *  trigger for a booked game and the send log.
 *
 *  OFF by default. With the switch off nothing on this venue changes and the
 *  scheduled job skips it before it reads a single fan.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

// The venue reads these, so they are shown in the venue's own clock, not the
// browser's. The row itself is stored in UTC.
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-CA',
    { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZone: 'America/Toronto' }) : '');

const RESULT_LABEL = {
    sent: 'Sent',
    failed: 'Failed',
    sending: 'Sending',
    blocked_not_allowlisted: 'Blocked — not on the preview allow-list',
};

const Num = ({ label, value, onChange, testid, hint }) => (
    <label className="block">
        <span className="block text-xs font-semibold text-slate-500 mb-1">{label}</span>
        <Input type="number" value={value} data-testid={testid}
               onChange={(e) => onChange(e.target.value)} className="tabular-nums" />
        {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
);

const Switch = ({ label, on, onToggle, testid, hint, busy }) => (
    <div className="flex items-start gap-3">
        <Button type="button" size="sm" variant={on ? 'default' : 'outline'}
                disabled={busy} data-testid={testid} onClick={onToggle}>
            {on ? 'On' : 'Off'}
        </Button>
        <div>
            <p className="text-sm font-semibold">{label}</p>
            {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
    </div>
);

export default function RemotePlayBlock({ orgId }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [booking, setBooking] = useState('');
    const [sender, setSender] = useState('venue');
    const [audience, setAudience] = useState(0);

    const load = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get('/goal-remote/admin/settings',
                                      { params: { organization_id: orgId } });
            setData(res.data);
            setDraft(res.data.settings);
            setBooking((res.data.bookings || [])[0]?.id || '');
        } catch (_e) { toast.error('Could not load Play from anywhere'); }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    // How many players said yes to THIS sender, before anything is sent.
    useEffect(() => {
        if (!orgId) return;
        let gone = false;
        api.get('/goal-remote/admin/audience', { params: { organization_id: orgId, sender } })
            .then((res) => { if (!gone) setAudience(res.data.count || 0); })
            .catch(() => { if (!gone) setAudience(0); });
        return () => { gone = true; };
    }, [orgId, sender]);

    const save = async (patch) => {
        setBusy(true);
        try {
            const res = await api.put('/goal-remote/admin/settings',
                                      { organization_id: orgId, ...patch });
            setDraft(res.data.settings);
            await load();
            toast.success('Saved');
        } catch (_e) { toast.error('Could not save'); }
        finally { setBusy(false); }
    };

    const sendNow = async () => {
        if (!booking) return;
        setBusy(true);
        try {
            const res = await api.post('/goal-remote/admin/send-now',
                                       { organization_id: orgId, booking_id: booking,
                                         sender });
            const r = res.data.result;
            toast.success(r.enabled
                ? `Eligible ${r.eligible} · sent ${r.sent} · already ${r.already} · no consent ${r.no_consent} · blocked ${r.blocked} · failed ${r.failed}`
                : 'Play from anywhere is off — nothing was sent');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail === 'tapelligence_is_super_admin_only'
                ? t('goalGame.sendAsSuperOnly') : 'Could not send');
        }
        finally { setBusy(false); }
    };

    if (!draft || !data) {
        return <p className="text-sm text-slate-500" data-testid="remote-admin-loading">…</p>;
    }

    return (
        <div className="space-y-5" data-testid="remote-admin">
            <div className="flex flex-wrap items-center gap-3">
                <Badge variant={draft.enabled ? 'default' : 'secondary'}
                       data-testid="remote-admin-state">{draft.enabled ? 'On' : 'Off'}</Badge>
                <Button variant={draft.enabled ? 'outline' : 'default'} disabled={busy}
                        data-testid="remote-admin-toggle"
                        onClick={() => save({ enabled: !draft.enabled })}>
                    {draft.enabled ? 'Turn off' : 'Turn on'}
                </Button>
                <p className="text-xs text-slate-500">
                    Off by default. With it off, nothing is sent and nothing changes for this venue.
                </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
                <Num label="Send the link (minutes before start)"
                     value={draft.send_minutes_before} testid="remote-minutes"
                     hint="Default 60"
                     onChange={(v) => setDraft({ ...draft, send_minutes_before: v })} />
                <Num label="Prize valid for (days)" value={draft.prize_validity_days}
                     testid="remote-validity" hint="0 = next game only"
                     onChange={(v) => setDraft({ ...draft, prize_validity_days: v })} />
                <Num label="Scratches per player per game"
                     value={draft.max_scratches_per_game} testid="remote-scratches"
                     hint="Default 1"
                     onChange={(v) => setDraft({ ...draft, max_scratches_per_game: v })} />
            </div>

            <Switch label="Attendance points for players at home"
                    hint="Off by default. Team-goal and star points still count."
                    on={!!draft.attendance_points} busy={busy}
                    testid="remote-attendance-toggle"
                    onToggle={() => save({ attendance_points: !draft.attendance_points })} />

            <Button disabled={busy} data-testid="remote-save"
                    onClick={() => save({
                        send_minutes_before: Number(draft.send_minutes_before),
                        prize_validity_days: Number(draft.prize_validity_days),
                        max_scratches_per_game: Number(draft.max_scratches_per_game),
                    })}>
                {busy ? <Loader2 className="animate-spin mr-2" size={15} />
                      : <Save className="mr-2" size={15} />}
                Save
            </Button>

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                <p className="text-sm font-semibold">
                    <Send size={14} className="inline mr-1 -mt-0.5" />Send the game-day email by hand
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    <select value={booking} onChange={(e) => setBooking(e.target.value)}
                            data-testid="remote-booking"
                            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                        {(data.bookings || []).length === 0 && <option value="">No booked game</option>}
                        {(data.bookings || []).map((b) => (
                            <option key={b.id} value={b.id}>
                                {b.label} — {when(b.start_time_utc)}
                            </option>
                        ))}
                    </select>
                    {/* WHO the email comes from, and how many players said yes
                        to that sender — read before anything is sent. */}
                    <select value={sender} onChange={(e) => setSender(e.target.value)}
                            data-testid="remote-sender"
                            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                        <option value="venue">{t('goalGame.sendAsVenue')}</option>
                        <option value="tapelligence">{t('goalGame.sendAsTapelligence')}</option>
                    </select>
                    <span className="text-xs text-slate-500" data-testid="remote-audience">
                        {t('goalGame.sendAudience', { count: audience })}
                    </span>
                    <Button variant="outline" disabled={busy || !booking}
                            data-testid="remote-send-now" onClick={sendNow}>
                        Send now
                    </Button>
                </div>
                <p className="text-xs text-slate-500" data-testid="remote-allowlist">
                    Preview allow-list: {(data.allow_list || []).join(', ') || 'empty — nothing can be emailed'}
                </p>
            </div>

            <div>
                <p className="text-sm font-semibold mb-2">
                    <Mail size={14} className="inline mr-1 -mt-0.5" />Send log
                </p>
                {(data.sends || []).length === 0 ? (
                    <p className="text-sm text-slate-500" data-testid="remote-sends-empty">
                        Nothing has been sent yet.
                    </p>
                ) : (
                    <ul className="text-xs space-y-1" data-testid="remote-sends">
                        {data.sends.map((s) => (
                            <li key={s.id} className="flex flex-wrap gap-2" data-testid={`remote-send-${s.id}`}>
                                <span className="text-slate-500 whitespace-nowrap">{when(s.sent_at)}</span>
                                <span className="font-medium">{s.email}</span>
                                <span className="text-slate-500">game {s.game_id}</span>
                                <span>{RESULT_LABEL[s.result] || s.result}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
